"use client";

import {
  ArrowRight,
  BarChart3,
  Cable,
  ChevronDown,
  LayoutDashboard,
  ListChecks,
  MapPin,
  MessageSquareText,
  MoreHorizontal,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

import { usePublicT } from "../../public-locale";
import styles from "./ProductPreview.module.css";

const NAVIGATION = [
  ["Today", "today"],
  ["Reviews", "reviews"],
  ["Local SEO", "localSeo"],
  ["Performance", "performance"],
  ["Priority Actions", "actions"],
  ["Integrations", "integrations"],
] as const;

type PreviewIconName = (typeof NAVIGATION)[number][1] | "spark";

const PREVIEW_ICONS: Record<PreviewIconName, LucideIcon> = {
  today: LayoutDashboard,
  reviews: MessageSquareText,
  localSeo: MapPin,
  performance: BarChart3,
  actions: ListChecks,
  integrations: Cable,
  spark: Sparkles,
};

function Icon({ name }: { name: PreviewIconName }) {
  const PreviewIcon = PREVIEW_ICONS[name];

  return (
    <PreviewIcon
      aria-hidden="true"
      className={styles.icon}
      strokeWidth={1.75}
      absoluteStrokeWidth
    />
  );
}

function KpiCard({
  icon,
  label,
  value,
  delta,
  tone,
}: {
  icon: "today" | "reviews" | "localSeo" | "actions";
  label: string;
  value: string;
  delta: string;
  tone: "violet" | "amber" | "blue" | "purple";
}) {
  const t = usePublicT();
  return (
    <div className={styles.kpiCard}>
      <div className={styles.kpiTop}>
        <span className={styles.kpiIcon} data-tone={tone}>
          <Icon name={icon} />
        </span>
        <span className={styles.kpiMenu} aria-hidden="true"><MoreHorizontal size={12} strokeWidth={1.7} absoluteStrokeWidth /></span>
      </div>
      <span className={styles.kpiLabel}>{t(label)}</span>
      <div className={styles.kpiValueRow}>
        <strong>{t(value)}</strong>
        <span className={styles.delta}>{t(delta)}</span>
      </div>
    </div>
  );
}

export default function ProductPreview() {
  const t = usePublicT();

  return (
    <div className={styles.stage}>
      <div className={styles.glow} aria-hidden="true" />

      <div className={styles.window}>
        <div className={styles.browserBar}>
          <div className={styles.browserLights} aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <div className={styles.browserAddress}>app.optrixislocal.com</div>
          <div className={styles.browserMeta}>{t("Live product preview")}</div>
        </div>

        <div className={styles.app}>
          <aside className={styles.sidebar}>
            <div className={styles.brand}>
              <img src="/brand/optrixis-local.svg" alt="" />
            </div>

            <div className={styles.workspace}>
              <div>
                <strong>{t("Bella Vita Ristorante")}</strong>
                <span>{t("Downtown location")}</span>
              </div>
              <span className={styles.chevron} aria-hidden="true"><ChevronDown size={12} strokeWidth={1.75} absoluteStrokeWidth /></span>
            </div>

            <div className={styles.navLabel}>{t("Workspace")}</div>
            <nav aria-label={t("Product preview navigation")}>
              {NAVIGATION.map(([label, icon], index) => (
                <div
                  key={label}
                  className={styles.navItem}
                  data-active={index === 0 ? "true" : undefined}
                >
                  <Icon name={icon} />
                  <span>{t(label)}</span>
                </div>
              ))}
            </nav>

            <div className={styles.sidebarFooter}>
              <span>{t("AI Local Growth Platform")}</span>
              <strong>{t("Pro workspace")}</strong>
            </div>
          </aside>

          <div className={styles.main}>
            <div className={styles.topbar}>
              <div className={styles.mobileBrand}>
                <img src="/brand/optrixis-local.svg" alt="" />
              </div>
              <div className={styles.topbarSpacer} />
              <span className={styles.sampleBadge}>{t("Sample data")}</span>
              <span className={styles.avatar}>BV</span>
            </div>

            <div className={styles.content}>
              <header className={styles.pageHeader}>
                <div>
                  <span className={styles.eyebrow}>OPTRIXIS LOCAL</span>
                  <h3>{t("Good morning, Bella")}</h3>
                  <p>{t("Here is what matters today for your local growth.")}</p>
                </div>
                <div className={styles.headerBadges}>
                  <span className={styles.googleBadge}>
                    <span className={styles.googleMark}>G</span>
                    {t("Google connected")}
                  </span>
                  <span className={styles.periodBadge}>{t("Last 28 days")}</span>
                </div>
              </header>

              <div className={styles.kpiGrid}>
                <KpiCard icon="today" label="Local Score" value="87" delta="+6" tone="violet" />
                <KpiCard icon="reviews" label="Reputation" value="4.8" delta="+0.2" tone="amber" />
                <KpiCard
                  icon="localSeo"
                  label="Google visibility"
                  value="18.4K"
                  delta="+18%"
                  tone="blue"
                />
                <KpiCard
                  icon="actions"
                  label="Priority actions"
                  value="3"
                  delta="2 critical"
                  tone="purple"
                />
              </div>

              <div className={styles.primaryGrid}>
                <section className={styles.panel}>
                  <div className={styles.panelHeading}>
                    <div>
                      <span>{t("Local growth overview")}</span>
                      <strong>{t("Performance signals")}</strong>
                    </div>
                    <span className={styles.panelPeriod}>{t("28 days")}</span>
                  </div>

                  <div className={styles.chart}>
                    {[38, 46, 43, 57, 52, 68, 64, 76, 71, 86, 82, 92].map((height, index) => (
                      <span
                        key={index}
                        style={{ height: `${height}%` }}
                        data-highlight={index > 8 ? "true" : undefined}
                      />
                    ))}
                    <div className={styles.chartLine} />
                  </div>

                  <div className={styles.performanceRows}>
                    <div>
                      <span>{t("Google Search visibility")}</span>
                      <strong>+18%</strong>
                    </div>
                    <div>
                      <span>{t("Direction requests")}</span>
                      <strong>+11%</strong>
                    </div>
                    <div>
                      <span>{t("Website clicks")}</span>
                      <strong>+9%</strong>
                    </div>
                  </div>
                </section>

                <section className={styles.aiPanel}>
                  <div className={styles.aiHeader}>
                    <span className={styles.aiIcon}>
                      <Icon name="spark" />
                    </span>
                    <div>
                      <span>{t("Optrixis AI")}</span>
                      <strong>{t("Recommended next action")}</strong>
                    </div>
                  </div>
                  <p>
                    {t(
                      "Weekend dinner visibility is down versus the previous period. Check opening hours and recent review themes before making a profile update."
                    )}
                  </p>
                  <div className={styles.aiAction}>
                    <span>{t("Priority")}</span>
                    <strong>{t("Review opening hours")}</strong>
                    <ArrowRight className={styles.actionArrow} aria-hidden="true" size={12} strokeWidth={1.8} absoluteStrokeWidth />
                  </div>
                </section>
              </div>

              <div className={styles.bottomRow}>
                <div className={styles.reviewCard}>
                  <span className={styles.reviewAvatar}>M</span>
                  <div className={styles.reviewBody}>
                    <div>
                      <strong>Maria G.</strong>
                      <span className={styles.stars}>★★★★★</span>
                    </div>
                    <p>{t("Excellent service and a great experience. We will be back.")}</p>
                  </div>
                  <span className={styles.replyReady}>{t("AI reply ready")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.reviewFloat}>
        <span>{t("New review")}</span>
        <strong>★★★★★</strong>
        <small>{t("Google · 3 min ago")}</small>
      </div>

      <div className={styles.scoreFloat}>
        <span>{t("Local Score")}</span>
        <strong>87</strong>
        <small>{t("+6 vs previous period")}</small>
      </div>

      <div className={styles.aiFloat}>
        <span className={styles.aiFloatDot}>
          <Icon name="spark" />
        </span>
        <div>
          <strong>{t("AI recommendation ready")}</strong>
          <small>{t("3 priority actions ranked")}</small>
        </div>
      </div>
    </div>
  );
}
